import { TailRecord, SectionResult, DocumentReference, CARRIER_KNOWLEDGE_BASE_MAP } from './types';
import { getUser } from './auth';
import { toError } from './errors';
import { timed } from './telemetry';

const API_BASE: string = process.env.REACT_APP_API_URL || '';

/** Ordered list of section IDs for sequential analysis. */
export const SECTION_IDS: string[] = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
];

/** Raw tail record shape from the API (snake_case). */
interface ApiTailRecord {
  tail_number: string;
  carrier: string;
  ata_code: string;
  discrepancy: string;
  station: string;
}

/** Build request headers with Cognito auth token. */
function buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const user = getUser();
  if (user?.idToken) {
    headers['Authorization'] = user.idToken;
  }
  return headers;
}

/** Map a snake_case API record to a camelCase TailRecord. */
function mapTailRecord(raw: ApiTailRecord): TailRecord {
  return {
    tailNumber: raw.tail_number,
    carrier: raw.carrier,
    ataCode: raw.ata_code,
    discrepancy: raw.discrepancy,
    station: raw.station,
  };
}

/**
 * Reject records the backend could not classify — empty or 'UNKNOWN' tail
 * numbers, or carriers without a known, provisioned Knowledge Base. Catching
 * these here prevents the UI from offering a tail that would fail during
 * analysis with "No knowledge base for carrier" or trigger a Bedrock API
 * error for an empty-string KB ID (rev-017).
 */
function isValidTailRecord(rec: TailRecord): boolean {
  const hasTail = !!rec.tailNumber && rec.tailNumber !== 'UNKNOWN';
  const hasKnownCarrier = !!rec.carrier && rec.carrier in CARRIER_KNOWLEDGE_BASE_MAP;
  const kbId = hasKnownCarrier ? CARRIER_KNOWLEDGE_BASE_MAP[rec.carrier] : '';
  return hasTail && hasKnownCarrier && !!kbId;
}

/**
 * Shared request helper that handles fetch + JSON parse + error propagation.
 * Every await is inside this single try/catch so callers can focus on
 * response shape rather than transport error handling (rev-014).
 *
 * Authentication guard: a 401 from the Cognito authorizer (expired id_token)
 * clears the stale session and redirects to re-authenticate. Dynamic import
 * of login() avoids a circular static dependency between api.ts and auth.ts.
 */
async function requestJson<T>(
  url: string,
  init: RequestInit,
  fallbackMessage: string,
): Promise<T> {
  try {
    const response = await fetch(url, init);

    // Expired Cognito token — clear stale session and force re-authentication.
    if (response.status === 401) {
      sessionStorage.removeItem('auth_user');
      const { login } = await import('./auth');
      await login();
      throw new Error('Session expired. Redirecting to sign in…');
    }

    // Reject non-2xx before attempting JSON parse (covers 403, 429, 500, etc.)
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `Request failed (HTTP ${response.status})`);
    }

    const data = (await response.json()) as T & { success?: boolean; error?: string };
    if (data && data.success === false) {
      throw new Error(data.error || fallbackMessage);
    }
    return data;
  } catch (err: unknown) {
    throw toError(err, fallbackMessage);
  }
}

/** Fetch all tail numbers currently in IN_WORK status from Athena. */
export async function fetchTailNumbers(): Promise<TailRecord[]> {
  try {
    return await timed('api.fetchTailNumbers', async () => {
      const data = await requestJson<{ tail_numbers: ApiTailRecord[] }>(
        `${API_BASE}/api/tail-numbers`,
        { headers: buildAuthHeaders() },
        'Failed to load tail numbers',
      );
      return (data.tail_numbers || []).map(mapTailRecord).filter(isValidTailRecord);
    });
  } catch (err: unknown) {
    throw toError(err, 'Failed to load tail numbers');
  }
}

/** Run a single analysis section against Bedrock for the given record. */
export async function runSection(
  record: TailRecord,
  sectionId: string,
): Promise<SectionResult> {
  const apiRecord: ApiTailRecord = {
    tail_number: record.tailNumber,
    carrier: record.carrier,
    ata_code: record.ataCode,
    discrepancy: record.discrepancy,
    station: record.station,
  };

  try {
    return await timed('api.runSection', async () => {
      const data = await requestJson<SectionResult & { sourceUris?: string[] }>(
        `${API_BASE}/api/analyze`,
        {
          method: 'POST',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ record: apiRecord, section: sectionId }),
        },
        `Section ${sectionId} failed`,
      );
      return {
        section: data.section,
        title: data.title,
        content: data.content,
        sourceUris: data.sourceUris || [],
      };
    }, { section: sectionId, carrier: record.carrier });
  } catch (err: unknown) {
    throw toError(err, `Section ${sectionId} failed`);
  }
}

/** Fetch a presigned URL for a document in the knowledge base S3 bucket. */
export async function fetchDocument(s3Uri: string): Promise<DocumentReference> {
  try {
    return await timed('api.fetchDocument', async () => {
      const data = await requestJson<DocumentReference>(
        `${API_BASE}/api/document?uri=${encodeURIComponent(s3Uri)}`,
        { headers: buildAuthHeaders() },
        'Failed to fetch document',
      );
      return { filename: data.filename, url: data.url };
    });
  } catch (err: unknown) {
    throw toError(err, 'Failed to fetch document');
  }
}
